import { useEffect } from 'react'
import useSWR, { Fetcher } from 'swr'
import { client } from '~/api/client'
import { Schemas, searchItemDetailsByImageSearchKey } from '~/api/gen'
import { Loading } from '~/components/elements/Loading'
import { ItemList } from '~/components/modules/ItemList'
import { sendGTMEvent } from '~/features/shared/gtmEvent/sendGTMEvent'
import { assertSuccess } from '~/lib/assertSuccess'
import { genderToSexId } from '~/lib/gender/converter'
import { Gender } from '~/lib/gender/types'
import { ScreenIds } from '~/types/screenIds'
import { NotFoundSimilarItem } from './NotFoundSimilarItem'

const fetchItemDetailsByImageSearchKey: Fetcher<
  Schemas.item_detail[],
  ['searchItemDetailsByImageSearchKey', string, Gender]
> = async ([_, searchKey, gender]) => {
  const sexId = genderToSexId(gender)
  const res = await searchItemDetailsByImageSearchKey(client, {
    parameter: {
      search_key: searchKey,
      sex_id: sexId === 0 ? undefined : sexId,
    },
  })
  assertSuccess(res)
  return res.data.item_details
}

type Props = {
  imageSearchKey: Schemas.image_search_key
  gender: Gender
  coordinateId: number
  setEventLabel: (searchConditions: string) => void
}

export const SimilarItemList: React.FC<Props> = ({
  imageSearchKey,
  gender,
  coordinateId,
  setEventLabel,
}) => {
  const { data, isValidating } = useSWR<
    Schemas.item_detail[],
    Error,
    ['searchItemDetailsByImageSearchKey', string, Gender]
  >(
    ['searchItemDetailsByImageSearchKey', imageSearchKey.search_key, gender],
    fetchItemDetailsByImageSearchKey
  )

  useEffect(() => {
    const eventLabel = `${gender}_${imageSearchKey.category.id}`
    sendGTMEvent({
      event: '201911_similarimages_analysisresult_item',
      eventCategory: 'imp',
      eventAction: 'similarimages_analysisresult_item',
      eventLabel: eventLabel,
    })
    setEventLabel(eventLabel)
  }, [imageSearchKey.category.id, setEventLabel, gender])

  return (
    <>
      {isValidating && (
        <div className="flex size-full justify-center pt-[50px]">
          <Loading />
        </div>
      )}
      {!isValidating && data && data.length > 0 && (
        <ItemList
          data={data}
          affiliateObject={{
            objectId: coordinateId,
            screenId: ScreenIds.CoordinateDetail,
          }}
        />
      )}
      {!isValidating && data && data.length === 0 && (
        <NotFoundSimilarItem className="p-[10px] pt-[39px]" />
      )}
    </>
  )
}
